import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Dates } from 'app/core/utils/dates';
import { ExternalAssetOwner } from 'app/loans/services/external-asset-owner';
import { ExternalAssetOwnerService } from 'app/loans/services/external-asset-owner.service';
import { SettingsService } from 'app/settings/settings.service';
import { CancelDialogComponent } from 'app/shared/cancel-dialog/cancel-dialog.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgClass } from '@angular/common';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { ExternalIdentifierComponent } from '../../shared/external-identifier/external-identifier.component';
import { MatDivider } from '@angular/material/divider';
import { MatTooltip } from '@angular/material/tooltip';
import { DateFormatPipe } from '../../pipes/date-format.pipe';
import { FormatNumberPipe } from '../../pipes/format-number.pipe';
import { STANDALONE_SHARED_IMPORTS } from 'app/standalone-shared.module';

@Component({
  selector: 'LOSx-investors',
  templateUrl: './investors.component.html',
  styleUrls: ['./investors.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  imports: [
    ...STANDALONE_SHARED_IMPORTS,
    FaIconComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    NgClass,
    ExternalIdentifierComponent,
    MatDivider,
    MatTooltip,
    MatPaginator,
    DateFormatPipe,
    FormatNumberPipe
  ]
})
export class InvestorsComponent implements OnInit {
  private settingsService = inject(SettingsService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private externalAssetOwner = inject(ExternalAssetOwner);
  private externalAssetOwnerService = inject(ExternalAssetOwnerService);
  private dateUtils = inject(Dates);

  /** Minimum transaction date allowed. */
  minDate = new Date(2000, 0, 1);
  /** Maximum transaction date allowed. */
  maxDate = new Date();

  searchResults: any[] = [];
  searchText = new UntypedFormControl('');
  effectiveFromDate = new UntypedFormControl('');
  effectiveToDate = new UntypedFormControl('');
  settlementFromDate = new UntypedFormControl('');
  settlementToDate = new UntypedFormControl('');

  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  existsDataToFilter = false;
  expandedRowId: number | null;

  totalRows: number;
  isLoading = false;

  pageSize = 50;
  currentPage = 0;
  filterText = '';

  sortAttribute = '';
  sortDirection = '';

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  /** Entry type filter form control. */
  entryTypeFilter = new UntypedFormControl('');
  /** Entry type filter data. */
  entryTypeFilterData = [
    {
      option: 'All',
      value: ''
    },
    {
      option: 'Sell',
      value: true
    },
    {
      option: 'Buyed Back',
      value: false
    }
  ];

  /** Columns to be displayed in investors table. */
  displayedColumns: string[] = [
    'status',
    'effectiveFrom',
    'ownerExternalId',
    'loanAccount',
    'transferExternalId',
    'settlementDate',
    'purchasePriceRatio',
    'totalAmount',
    'actions'
  ];

  ngOnInit(): void {
    this.maxDate = this.settingsService.maxAllowedDate;
    this.searchEAO();
  }

  itemStatus(status: string): string {
    return this.externalAssetOwner.itemStatus(status);
  }

  isPending(item: any): boolean {
    return this.externalAssetOwner.isPending(item);
  }

  isPendingOrCanceled(item: any): boolean {
    return this.externalAssetOwner.isPendingOrCanceled(item);
  }

  isBuyBackPending(item: any): boolean {
    return this.externalAssetOwner.isBuyBackPending(item);
  }

  canBeCancelled(item: any): boolean {
    return this.externalAssetOwner.validateStatus(item, 'PENDING');
  }

  canBeSold(item: any): boolean {
    return this.externalAssetOwner.canBeSold(item);
  }

  canBeBuyed(item: any): boolean {
    return this.externalAssetOwner.validateStatus(item, 'ACTIVE');
  }

  searchEAO(): void {
    this.isLoading = true;
    const payload: any = {
      request: {},
      page: this.currentPage,
      size: this.pageSize
    };
    const dateFormat = 'yyyy-MM-dd';
    const request: any = {};
    if (this.searchText.value) {
      request['text'] = this.searchText.value;
    }
    if (this.effectiveFromDate.value) {
      request['effectiveFromDate'] = this.dateUtils.formatDate(this.effectiveFromDate.value, dateFormat);
    }
    if (this.effectiveToDate.value) {
      request['effectiveToDate'] = this.dateUtils.formatDate(this.effectiveToDate.value, dateFormat);
    }
    if (this.settlementFromDate.value) {
      request['settlementFromDate'] = this.dateUtils.formatDate(this.settlementFromDate.value, dateFormat);
    }
    if (this.settlementToDate.value) {
      request['settlementToDate'] = this.dateUtils.formatDate(this.settlementToDate.value, dateFormat);
    }
    payload['request'] = request;
    this.externalAssetOwnerService.searchExternalAssetOwnerTransfer(payload).subscribe((response: any) => {
      this.totalRows = response.totalElements;
      this.existsDataToFilter = response.totalElements > 0;
      this.dataSource.data = response.content;
      this.searchResults = response.content;
      this.isLoading = false;
    });
  }

  transform(data: any): any {
    return data;
  }

  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.searchEAO();
  }

  cancelPendingSale(transfer: any): void {
    const deleteDataTableDialogRef = this.dialog.open(CancelDialogComponent, {
      data: { cancelContext: `the Asset Transfer with the Owner External Id ${transfer.owner.externalId} ` }
    });
    deleteDataTableDialogRef.afterClosed().subscribe((response: any) => {
      if (response.cancel) {
        const payload: any = {
          transferExternalId: transfer.transferExternalId
        };
        this.externalAssetOwnerService
          .executeExternalAssetOwnerTransferCommand(transfer.transferId, payload, 'cancel')
          .subscribe((result: any) => {
            this.reload();
          });
      }
    });
  }

  reload() {
    const url: string = this.router.url;
    this.router.navigateByUrl(`/`, { skipLocationChange: true }).then(() => this.router.navigate([url]));
  }

  private resetPaginator() {
    this.currentPage = 0;
    this.paginator.firstPage();
  }
}
